import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from 'api/ApiClient';
import { QueryKeys } from 'entities/user/api/common';
import type { ProfileResponse } from 'entities/user/types';
import { DashboardEvent } from 'features/dashboard/lib/constants';
import { trackEvent } from 'shared/lib/analytics/trackEvent';

export type CompleteProfileValues = {
  first_name: string;
  last_name: string;
  company_name: string;
};

type UseCompleteProfileParams = {
  profile: ProfileResponse | undefined;
  askName: boolean;
  askCompanyName: boolean;
};

export const useCompleteProfile = ({ profile, askName, askCompanyName }: UseCompleteProfileParams) => {
  const queryClient = useQueryClient();

  const reportSaveError =
    (failedRequest: 'company' | 'profile') =>
    (err: unknown): never => {
      trackEvent(DashboardEvent.COMPLETE_PROFILE_SAVE_ERROR, {
        asked_name: askName,
        asked_company_name: askCompanyName,
        failed_request: failedRequest,
      });
      throw err;
    };

  // The company goes first so the profile read afterwards already carries its name.
  return useMutation({
    mutationFn: async ({ company_name, ...name }: CompleteProfileValues): Promise<ProfileResponse> => {
      if (askCompanyName && profile?.company) {
        await apiClient
          .updateCompanySettings(profile.company.id, { name: company_name.trim() })
          .catch(reportSaveError('company'));
      }
      return (askName ? apiClient.updateProfile(name) : apiClient.getProfile()).catch(reportSaveError('profile'));
    },
    // AuthGuard reads the live profile, so writing the response to the cache moves the user on.
    onSuccess: (updated) => queryClient.setQueryData([QueryKeys.GetProfile], updated),
  });
};
